import React from "react";
import MarketingLayout, { SpaLink } from "./MarketingLayout";

const LAST_UPDATED = "July 2, 2026";

const INTRO =
  'This Privacy Policy explains how GearCheckAuto ("we", "us", or "our") collects, uses, and ' +
  "shares information when you use our website and mobile apps (the \"Service\").";

// Prose lives in strings (rendered via {}) rather than JSX text so apostrophes/quotes are not
// an eslint no-unescaped-entities concern and the content stays easy to edit.
const SECTIONS = [
  {
    heading: "Information we collect",
    body: ["We collect the information you provide and some technical data needed to run the Service:"],
    bullets: [
      "Account and profile: your name, email address, phone number, password (stored hashed), and optional postal address (street, city, state, ZIP) and time zone.",
      "Shop profile (for repair shops and mobile mechanics): business name, service specializations, service area (home-base ZIP and radius), business website and social links, and a shop logo. Shop addresses are converted to map coordinates so shops can be shown on the map.",
      "Messages and attachments: the content of requests and messages you exchange with shops, and any photos, videos, or PDFs you attach. Photos are stripped of location (EXIF/GPS) data on your device before upload.",
      "Location: the ZIP codes you enter to search for shops, and (for shops) the geocoded location of your service area.",
      "Notifications: web-push subscriptions and mobile push tokens for the device you use, so we can send you alerts.",
      "Payments (shops only): subscription billing is processed by Stripe. We store your subscription status and Stripe identifiers, not your full card details.",
      "Security: if you enable two-factor authentication, we store an encrypted authentication secret and hashed recovery codes.",
      "Usage and technical data: presence/last-active status, your notification preferences, and standard technical information (such as IP address and device or browser type) collected by our hosting and service providers.",
      "On your device: we store a sign-in token and your theme preference in local storage to keep you signed in and remember settings.",
    ],
  },
  {
    heading: "How we use information",
    body: ["We use the information to:"],
    bullets: [
      "Operate the Service and connect vehicle owners with nearby shops and mobile mechanics.",
      "Route your requests and messages and show shops on a map.",
      "Send notifications and transactional emails (for example, new messages, verification, and reminders) according to your preferences.",
      "Process shop subscriptions and manage accounts.",
      "Provide support, maintain security, prevent fraud and abuse, and improve the Service.",
    ],
  },
  {
    heading: "How we share information",
    body: ["We do not sell your personal information. We share it only as needed to run the Service:"],
    bullets: [
      "Shops you contact: when you send a request or message, the shops you select receive its content and the information they need to respond (such as your city or ZIP, vehicle details, display name, and messages). A mobile mechanic's exact street address is not shown to customers - only an approximate service area.",
      "Service providers: we use trusted providers to operate the Service, including Stripe (payments), Postmark (email), Firebase Cloud Messaging and web push (notifications), Mapbox (maps), and cloud object storage (attachments), along with our hosting infrastructure. We may also use automated tools, including AI, to help review and route requests.",
      "Legal and safety: we may disclose information if required by law or to protect the rights, safety, and security of our users or the Service.",
    ],
  },
  {
    heading: "Data retention",
    body: [
      "We keep your information while your account is active. Message attachments automatically expire and are deleted about 30 days after upload. You can delete your account at any time from your profile; doing so removes or anonymizes your personal information and cancels any active subscription billing. Some records may be retained where required by law or for legitimate business purposes.",
    ],
  },
  {
    heading: "Your choices and rights",
    body: [
      "You can view and update your profile, manage notification settings, and delete your account from within the Service. Depending on where you live, you may have rights to access, correct, or delete your personal information, or to object to or restrict certain processing. To make a request, use the settings in the app or contact us using the details below.",
    ],
  },
  {
    heading: "Security",
    body: [
      "We protect your information with industry-standard measures, including encryption in transit (HTTPS), hashed passwords, optional two-factor authentication, and encrypted storage of sensitive security data. No method of transmission or storage is completely secure, so we cannot guarantee absolute security.",
    ],
  },
  {
    heading: "Children's privacy",
    body: [
      "The Service is not directed to children under 13 (or the minimum age required in your location), and we do not knowingly collect personal information from them. If you believe a child has provided us information, please contact us so we can remove it.",
    ],
  },
  {
    heading: "International users",
    body: [
      "GearCheckAuto is operated in the United States, and your information may be processed in the United States or other countries where we or our service providers operate. By using the Service, you understand your information may be transferred to and processed in those locations.",
    ],
  },
  {
    heading: "Changes to this policy",
    body: [
      'We may update this Privacy Policy from time to time. When we do, we will revise the "Last updated" date above and, where appropriate, provide additional notice. Your continued use of the Service after an update means you accept the revised policy.',
    ],
  },
];

export default function PrivacyPage({ onNavigate }) {
  return (
    <MarketingLayout onNavigate={onNavigate}>
      <section className="bg-gradient-to-b from-blue-50 dark:from-blue-950/40 to-white px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <h1 className="text-4xl font-bold leading-tight tracking-tight text-slate-900 dark:text-gray-100 md:text-5xl">
            Privacy Policy
          </h1>
          <p className="mt-3 text-sm text-slate-500 dark:text-gray-400">Last updated: {LAST_UPDATED}</p>
          <p className="mt-5 text-lg leading-relaxed text-slate-600 dark:text-gray-300">{INTRO}</p>
        </div>
      </section>

      <article className="mx-auto max-w-3xl space-y-8 px-4 py-12 text-slate-700 dark:text-gray-200">
        {SECTIONS.map((s) => (
          <section key={s.heading}>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">{s.heading}</h2>
            {s.body.map((p, i) => (
              <p key={i} className="mt-3 leading-relaxed">{p}</p>
            ))}
            {s.bullets ? (
              <ul className="mt-3 list-disc space-y-2 pl-5">
                {s.bullets.map((b, i) => (
                  <li key={i} className="leading-relaxed">{b}</li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}

        <section>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">Contact us</h2>
          <p className="mt-3 leading-relaxed">
            If you have questions about this Privacy Policy or your information, contact us at{" "}
            <a
              href="mailto:support@gearcheckauto.com"
              className="font-semibold text-blue-700 dark:text-blue-300 hover:text-blue-600"
            >
              support@gearcheckauto.com
            </a>{" "}
            or through our{" "}
            <SpaLink
              view="contact"
              onNavigate={onNavigate}
              className="font-semibold text-blue-700 dark:text-blue-300 hover:text-blue-600"
            >
              Contact page
            </SpaLink>
            .
          </p>
        </section>
      </article>
    </MarketingLayout>
  );
}
